import {CacheService, CustomerService, IApplicationFilter, property, widget, WidgetPropertyType} from '@dynamik/common';
import {BindingEngine, computedFrom} from 'aurelia-binding';
import {WidgetBase} from '@dynamik/common';
import {customElement} from 'aurelia-templating';
import {CategoriesClient, GroupedTransactionsViewModel, TransactionsClient} from '@dynamik/common/clients/transaction-clients';
import {DisplayedFields} from '../displayed-fields';
import {CategoryType} from '../../../classes/category-type';

@customElement('processed-vouchers-light-widget')
@widget('Processed Vouchers (light)', true, false, 'Description')
export class ProcessedVouchersLightWidget extends WidgetBase {
    constructor(element: Element, customerService: CustomerService, private transactionsClient: TransactionsClient,
                bindingEngine: BindingEngine, private cacheService: CacheService, private categoriesClient: CategoriesClient) {
        super(element, bindingEngine, customerService);
    }

    allData: GroupedTransactionsViewModel[];
    data: GroupedTransactionsViewModel[];
    currentPage = 0;
    lastItemRendered: (value: unknown) => void;
    DisplayedFields = DisplayedFields;

    @property('Data', 'Portfolio', WidgetPropertyType.List)
    portfolio?: string;

    async portfolioListValues() {
        return [
            {value: undefined, label: 'All portfolios'},
            ...(await this.categoriesClient.getSecCategories(this.customerService.selectedCustomer.companyId, CategoryType.Portfolio))
                .map(x => ({value: x.name, label: x.name}))
        ];
    }

    portfolioChanged(newValue: string, oldValue: string) {
        if (!this.title) {
            return;
        }

        this.title = this.title.replace(` (${oldValue})`, '') + (newValue ? ` (${newValue})` : '');
    }

    @property('Data', undefined, WidgetPropertyType.List, [{value: 'lastTransactionDate', label: 'Date'}, {value: 'name', label: 'Name'}])
    sortBy: 'lastTransactionDate' | 'name' = 'lastTransactionDate';

    @property('Data')
    descending: boolean = true;

    @property('Data')
    quantityPrecision?: number = 0;

    @property('Data')
    precision: number = 2;

    @property('Table')
    font?: string;

    @property('Table')
    fontSize?: number = 11;

    @property('Table')
    color?: string;

    @property('Table')
    rowHeight?: number;

    @property('Table')
    rowsPerPage: number = 30;

    @property('Table')
    columnWidths?: string;

    @property()
    captionFont?: string;

    @property()
    captionFontSize?: number = 11;

    @property()
    captionColor?: string;

    @property('Data', 'Displayed fields', WidgetPropertyType.List)
    displayedFields?: DisplayedFields;

    async displayedFieldsListValues() {
        return [
            {value: undefined, label: 'Default'},
            {value: DisplayedFields.All, label: 'All fields'},
            {value: DisplayedFields.Fifo, label: 'FIFO'}
        ];
    }

    @computedFrom('displayedFields', 'customerService.selectedCustomer.useAverageCostPrice')
    get displayAvg() {
        return this.customerService.selectedCustomer.useAverageCostPrice && [undefined, DisplayedFields.All].includes(this.displayedFields);
    }

    @computedFrom('displayedFields', 'customerService.selectedCustomer.useAverageCostPrice')
    get displayFifo() {
        return !this.customerService.selectedCustomer.useAverageCostPrice || [DisplayedFields.Fifo, DisplayedFields.All].includes(this.displayedFields);
    }

    protected async loadData(filter: IApplicationFilter) {
        return await this.cacheService.getOrSet(`processed-vouchers-light-widget-${filter.customer.companyId}-${this.portfolio}-${filter.dates.fromStr}-${filter.dates.toStr}`,
            () => this.transactionsClient.grouped(filter.customer.companyId, undefined, false, undefined, undefined, filter.dates.fromStr, filter.dates.toStr, this.portfolio), 60000, false);
    }

    protected async setData(data: GroupedTransactionsViewModel[]) {
        // Populate excelData for right-click export
        this.excelData = {fileName: "Processed",
            data: {Items: data}}


        data.forEach(x => {
            x.quantity = Math.abs(x.quantity);
            x.amount = Math.abs(x.amount);
        });
        this.allData = [...data].sort((a, b) => a.lastDetail[this.sortBy].localeCompare(b.lastDetail[this.sortBy]) * (this.descending ? -1 : 1));
        this.updatePageData();
    }

    private updatePageData() {
        this.data = this.allData.filter((x, i) => this.currentPage * this.rowsPerPage <= i && i < (this.currentPage + 1) * this.rowsPerPage);
    }

    getPageCount(): number {
        return this.allData ? Math.ceil(this.allData.length / this.rowsPerPage) : 0;
    }

    async setPage(page: number): Promise<void> {
        if (page === this.currentPage) {
            return;
        }
        this.currentPage = page;
        this.updatePageData();
        await new Promise(resolve => this.lastItemRendered = resolve);
    }

    override configChanged(key: keyof ProcessedVouchersLightWidget, newValue: any, oldValue: any) {
        switch (key) {
            case 'rowsPerPage':
                this.updatePageData();
                break
            case 'portfolio':
                this.portfolioChanged(newValue, oldValue);
            case 'quantityPrecision':
            case 'precision':
            case 'sortBy':
            case 'descending':
                this.refresh$.next();
                break;
            default:
                break;
        }
    }
}
